/**
 * Podzavihek Po projektih (docs/ARHITEKTURA.md, 15.5): sredstva izbranega
 * projekta v mesecu z delezem, zneskom po odstotkih, knjizenim in ostalimi
 * projekti na sredstvu. Klik na knjizeno odpre knjizbe.
 */
import type { ProjektPoMesecu, SredstvoNaProjektu } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';

import { api } from '../../lib/api';
import { useBesedilaProjektov } from '../../lib/besedila-projektov';
import { znesek } from '../../lib/format';
import { odstotekZaPrikaz } from '../../strani/OdstotkiProjektov';
import { OpozorilaUvoza } from '../OpozorilaUvoza';
import { KnjizbeProjekta } from './KnjizbeProjekta';

interface Lastnosti {
  podjetje: string;
  /** Projekt iz naslova (povezava iz Pregleda). */
  zacetniProjekt?: string;
}

export function PoProjektihStranke({ podjetje, zacetniProjekt }: Lastnosti): JSX.Element {
  const b = useBesedilaProjektov();
  const [stanje, setStanje] = useState<ProjektPoMesecu | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [odprto, setOdprto] = useState<SredstvoNaProjektu | null>(null);

  const preberi = useCallback(
    (projekt?: string, mesec?: string): void => {
      api
        .projektiPoProjektih(podjetje, projekt, mesec)
        .then((s) => {
          setStanje(s);
          setNapaka(null);
        })
        .catch((e: unknown) => {
          setNapaka(e instanceof Error ? e.message : b.napakaBranja);
        });
    },
    [podjetje, b.napakaBranja],
  );
  useEffect(() => {
    preberi(zacetniProjekt);
  }, [preberi, zacetniProjekt]);

  if (napaka !== null) {
    return (
      <p className="napaka" role="alert">
        {napaka}
      </p>
    );
  }
  if (stanje === null) return <p className="drobno">{b.berem}</p>;
  if (stanje.projekt === null) return <p className="drobno">{b.niProjektov}</p>;

  return (
    <>
      <section className="kartica">
        <p className="podnaslov">{b.poProjektihUvod}</p>
        <div className="vrstica" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <label>
            {b.projekt}{' '}
            <select
              aria-label={b.projekt}
              value={stanje.projekt.id}
              onChange={(d) => {
                setOdprto(null);
                preberi(d.target.value, stanje.mesec ?? undefined);
              }}
            >
              {stanje.projekti.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.naziv}
                  {p.aktiven ? '' : ` (${b.zakljucen})`}
                </option>
              ))}
            </select>
          </label>
          <label>
            {b.mesec}{' '}
            <input
              type="month"
              aria-label={b.mesec}
              value={stanje.mesec ?? ''}
              onChange={(d) => {
                if (d.target.value === '') return;
                setOdprto(null);
                preberi(stanje.projekt?.id, d.target.value);
              }}
            />
          </label>
        </div>
        <OpozorilaUvoza opozorila={stanje.opozorila} />
        {stanje.sredstva.length === 0 ? (
          <p className="drobno">{b.niSredstev}</p>
        ) : (
          <div className="tabela-ovoj">
            <table>
              <thead>
                <tr>
                  <th>{b.sredstvo}</th>
                  <th>{b.naziv}</th>
                  <th className="stevilka">{b.delez}</th>
                  <th className="stevilka">{b.amortizacija}</th>
                  <th className="stevilka">{b.poOdstotkih}</th>
                  <th className="stevilka">{b.knjizeno}</th>
                  <th>{b.ostaliProjekti}</th>
                </tr>
              </thead>
              <tbody>
                {stanje.sredstva.map((s) => (
                  <tr key={s.sredstvo}>
                    <td>
                      <code>{s.sredstvo}</code>
                    </td>
                    <td>{s.naziv ?? ''}</td>
                    <td className="stevilka">{s.odstotek === null ? '—' : odstotekZaPrikaz(s.odstotek)}</td>
                    <td className="stevilka">{znesek(s.amortizacija)}</td>
                    <td className="stevilka">{znesek(s.cilj)}</td>
                    <td className="stevilka">
                      {s.knjizbe.length === 0 ? (
                        znesek(s.knjizeno)
                      ) : (
                        <button
                          type="button"
                          className="gumb-besedilo"
                          aria-label={`${b.knjizeno} ${s.sredstvo}`}
                          onClick={() => {
                            setOdprto(s);
                          }}
                        >
                          {znesek(s.knjizeno)}
                        </button>
                      )}
                    </td>
                    <td className="drobno">{s.ostali.map((o) => `${o.naziv} ${odstotekZaPrikaz(o.odstotek)}`).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>{b.skupaj}</th>
                  <td />
                  <td />
                  <td />
                  <td className="stevilka">
                    <strong>{znesek(stanje.skupaj.cilj)}</strong>
                  </td>
                  <td className="stevilka">
                    <strong>{znesek(stanje.skupaj.knjizeno)}</strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
      {odprto !== null && (
        <KnjizbeProjekta
          opis={`${odprto.sredstvo} ${odprto.naziv ?? ''}`}
          knjizbe={odprto.knjizbe}
          naZapri={() => {
            setOdprto(null);
          }}
        />
      )}
    </>
  );
}
